import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import type { BreakdownPoint } from "@/core/analytics/analytics-utils";
import { formatMoneyCompact } from "@/core/pharmacy/format";

const COLORS = ["#7c3aed", "#0d9488", "#14b8a6", "#f59e0b", "#3b82f6", "#ec4899", "#6366f1", "#10b981"];

interface RevenueSplitChartProps {
  data: BreakdownPoint[];
  emptyLabel?: string;
  valueFormatter?: (value: number) => string;
  /** Affiche tous les libellés dans la légende, y compris les montants à 0. */
  showZeroValues?: boolean;
}

function toChartItem(d: BreakdownPoint) {
  return {
    name: d.name.length > 22 ? `${d.name.slice(0, 20)}…` : d.name,
    value: d.value,
    fullName: d.name,
  };
}

export function RevenueSplitChart({
  data,
  emptyLabel = "Aucune donnée sur la période.",
  valueFormatter = formatMoneyCompact,
  showZeroValues = false,
}: RevenueSplitChartProps) {
  const legendData = (showZeroValues ? data : data.filter((d) => d.value > 0)).map(toChartItem);
  const pieData = data.filter((d) => d.value > 0).map(toChartItem);

  if (legendData.length === 0) {
    return <div className="ph-chart-empty text-muted">{emptyLabel}</div>;
  }

  return (
    <div className="ph-chart-wrap ph-chart-wrap--pie">
      {pieData.length > 0 ? (
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius={58}
              outerRadius={88}
              paddingAngle={3}
              dataKey="value"
            >
              {pieData.map((item, index) => {
                const colorIndex = legendData.findIndex((d) => d.fullName === item.fullName);
                return (
                  <Cell
                    key={item.fullName}
                    fill={COLORS[(colorIndex >= 0 ? colorIndex : index) % COLORS.length]}
                    stroke="none"
                  />
                );
              })}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const p = payload[0].payload as { fullName: string; value: number };
                return (
                  <div
                    style={{
                      padding: "8px 12px",
                      borderRadius: 8,
                      border: "1px solid #e2e8f0",
                      background: "#fff",
                      fontSize: 13,
                    }}
                  >
                    <div className="fw-semibold">{p.fullName}</div>
                    <div>{valueFormatter(p.value)}</div>
                  </div>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <div className="ph-chart-empty text-muted small mb-2">{emptyLabel}</div>
      )}
      <ul className="ph-pie-legend">
        {legendData.map((item, i) => (
          <li key={item.fullName} className={item.value <= 0 ? "text-muted" : undefined}>
            <span
              className="ph-pie-dot"
              style={{
                background:
                  item.value > 0 ? COLORS[i % COLORS.length] : "transparent",
                border: item.value <= 0 ? "1px solid #cbd5e1" : undefined,
              }}
            />
            <span className="ph-pie-name">{item.fullName}</span>
            <span className="ph-pie-val">{valueFormatter(item.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
